import React, { CSSProperties } from 'react'
import { Button as MaterialButton } from '@mui/material'
import { RobotenIcon } from '../RobotenIcon'
import { CssStyles, IconName, NamedButtonColor } from '../../types'
import { getClassName, parseClassNames } from '../../utils'
import { css, cx } from '@emotion/css'

export interface IButton {
  /** If true, the base button will be disabled.
   *
   * @default false
   */
  disabled?: boolean

  /** The color of the component.
   *
   * @default "primary"
   */
  color?: NamedButtonColor

  /** If true, no elevation is used.
   *
   * @default false
   */
  disableElevation?: boolean

  /** Element placed before the children.
   */
  startIcon?: IconName

  /** Element placed after the children.
   *
   */
  endIcon?: IconName

  /** If true, the button will take up the full width of its container.
   *
   * @default false
   */
  fullWidth?: boolean

  /** The URL to link to when the button is clicked. If defined, an a
   * element will be used as the root node.
   *
   */
  href?: string

  /** The size of the component.
   *
   * @default "medium"
   */
  size?: 'tiny' | 'small' | 'medium' | 'large'

  /** The text to display in the button.
   *
   * @default "Button"
   */
  text?: string

  /** The variant to use
   *
   *  @default "text"
   */
  variant?: 'contained' | 'outlined' | 'text'

  /**
   * Type of the button, submit can be used within a Form to trigget submit.
   * @default "button"
   */
  type: 'button' | 'submit'

  /** Provide an optional function to be called when the button is clicked */
  onClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void

  /** Provide an optional function to be called when the button is focused */
  onFocus?: (evt: React.FocusEvent<HTMLButtonElement>) => void

  /** *Deprecated* Raw CSS styles
   * @deprecated
   */
  styles?: CssStyles<'root' | 'startIcon' | 'endIcon'>

  /** If true, shows arrow dropdown as right icon.
   *
   * @default false
   */
  hasMenu?: boolean

  style?: CSSProperties | string
  startIconStyle?: CSSProperties | string
  endIconStyle?: CSSProperties | string
}

export const Button: React.FC<IButton> = React.forwardRef<
  HTMLButtonElement,
  IButton
>(
  (
    {
      color = 'primary',
      disableElevation = true,
      variant = 'text',
      text = 'Button',
      size = 'medium',
      fullWidth = false,
      endIcon,
      startIcon,
      styles,
      style,
      startIconStyle,
      endIconStyle,
      hasMenu,
      ...rest
    },
    ref
  ) => {
    const classNames = styles && parseClassNames(styles)

    return (
      <MaterialButton
        ref={ref}
        color={color}
        disableElevation={disableElevation}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        startIcon={
          startIcon && (
            <RobotenIcon
              materialFallback
              iconName={startIcon}
              styles={
                styles?.startIcon ? { root: styles.startIcon } : undefined
              }
              size={size === 'tiny' ? 16 : undefined}
              style={startIconStyle}
            />
          )
        }
        endIcon={
          endIcon ? (
            <RobotenIcon
              materialFallback
              iconName={endIcon}
              styles={styles?.endIcon ? { root: styles.endIcon } : undefined}
              size={size === 'tiny' ? 16 : undefined}
              style={endIconStyle}
            />
          ) : hasMenu ? (
            <RobotenIcon
              materialFallback
              iconName="arrow_drop_down"
              size={18}
              styles={styles?.endIcon ? { root: styles.endIcon } : undefined}
              style={endIconStyle}
            />
          ) : undefined
        }
        className={cx(classNames?.root, getClassName(style))}
        disableRipple
        classes={{
          root: css({
            lineHeight: size === 'tiny' ? '16px' : 1.75,
          }),
          endIcon: css({
            marginLeft: hasMenu && !endIcon ? '2px' : undefined,
          }),
          contained: css({
            paddingRight: hasMenu && !endIcon ? '8px' : undefined,
          }),
          outlined: css({
            paddingRight: hasMenu && !endIcon ? '8px' : undefined,
          }),
        }}
        {...rest}
      >
        {text}
      </MaterialButton>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  Button.displayName = 'Button'
}
